import { CircularProgress, IconButton } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";

import React from "react";
import { MaterialCompletionArgs } from "../../types";
import { useCraneDriver } from "../../CraneDriverProvider";
import { adjustButtonSx } from "./CraneCellAdjustButton";

interface CraneTableSubmitButtonProps {
  completeArgs: MaterialCompletionArgs;
  setShowHoldDialog: (show: boolean) => void;
  isCompleted: boolean;
  isSubmitting: boolean;
}

export const CraneCellSubmitButton = ({
  completeArgs,
  setShowHoldDialog,
  isCompleted,
  isSubmitting,
}: CraneTableSubmitButtonProps) => {
  const { submitSingleMaterial, submittingBasketId, submittingMaterialIds } =
    useCraneDriver();
  const theme = useTheme();

  const [isPressingComplete, setIsPressingComplete] = React.useState(false);
  const pressTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const tapCountRef = React.useRef(0);
  const lastTapTimeRef = React.useRef(0);

  const handleCompletePressStart = (args: MaterialCompletionArgs) => {
    const now = Date.now();
    if (now - lastTapTimeRef.current < 3000) {
      tapCountRef.current++;
      if (tapCountRef.current >= 3) {
        setShowHoldDialog(true);
        tapCountRef.current = 0;
        return;
      }
    } else {
      tapCountRef.current = 1;
    }
    lastTapTimeRef.current = now;

    setIsPressingComplete(true);
    pressTimeoutRef.current = setTimeout(() => {
      if (pressTimeoutRef.current) {
        void submitSingleMaterial(args).then(() => {
          setIsPressingComplete(false);
          pressTimeoutRef.current = null;
        });
      }
    }, 500);
  };

  const handleCompletePressEnd = () => {
    if (pressTimeoutRef.current) {
      clearTimeout(pressTimeoutRef.current);
      pressTimeoutRef.current = null;
    }
    setIsPressingComplete(false);
  };

  React.useEffect(() => {
    return () => {
      if (pressTimeoutRef.current) {
        clearTimeout(pressTimeoutRef.current);
      }
    };
  }, []);

  return (
    <IconButton
      className="material-complete-btn"
      onMouseDown={() => {
        handleCompletePressStart(completeArgs);
      }}
      onMouseUp={handleCompletePressEnd}
      onMouseLeave={handleCompletePressEnd}
      onTouchStart={() => {
        handleCompletePressStart(completeArgs);
      }}
      onTouchEnd={handleCompletePressEnd}
      sx={{
        ...adjustButtonSx,
        bgcolor: isPressingComplete ? "success.main" : "grey.200",
        "&:hover": {
          bgcolor: isPressingComplete ? "success.dark" : "success.main",
        },
        transform: isPressingComplete ? "scale(0.95)" : "scale(1)",
        transition: "transform 0.1s ease, background-color 0.1s ease",
      }}
      disabled={
        isCompleted ||
        isSubmitting ||
        submittingBasketId !== null ||
        submittingMaterialIds.size > 0
      }
    >
      {isSubmitting ? (
        <CircularProgress size={28} color="success" />
      ) : (
        <CheckIcon
          sx={{
            fontSize: { xs: 28, md: 32, lg: 40 },
            color: theme.palette.success.dark,
          }}
        />
      )}
    </IconButton>
  );
};
